import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/Logo';
import { Menu, X, Globe } from 'lucide-react';

const PATH_MAP = {
  ar: { privacy: 'privacidad', cookies: 'cookies', terms: 'terminos' },
  en: { privacy: 'privacy', cookies: 'cookies', terms: 'terms' },
};

export default function Header({ lang = 'ar' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); setLangOpen(false); }, [location.pathname]);

  const switchLanguage = (target) => {
    // Replace lang segment while preserving current sub-page mapping.
    const parts = location.pathname.split('/').filter(Boolean);
    if (['ar', 'en'].includes(parts[0])) parts.shift();
    const currentSub = parts.join('/');
    // Map slugs between languages
    const slugMap = {
      privacidad: 'privacy', privacy: 'privacidad',
      terminos: 'terms', terms: 'terminos',
      cookies: 'cookies',
    };
    let mapped = currentSub;
    if (slugMap[currentSub]) {
      // If target lang uses the other spelling, swap.
      const dictFor = PATH_MAP[target];
      if (currentSub === 'privacidad' || currentSub === 'privacy') mapped = dictFor.privacy;
      else if (currentSub === 'terminos' || currentSub === 'terms') mapped = dictFor.terms;
      else if (currentSub === 'cookies') mapped = dictFor.cookies;
    }
    const dest = `/${target}${mapped ? '/' + mapped : ''}`;
    navigate(dest, { replace: false });
  };

  const homePath = `/${lang}`;
  const anchor = (id) => `${homePath}#${id}`;

  const nav = [
    { label: t('nav.about'), to: anchor('about') },
    { label: t('nav.how'), to: anchor('how') },
    { label: t('nav.projects'), to: anchor('projects') },
    { label: t('nav.contact'), to: anchor('contact') },
  ];

  return (
    <header className="zt-header">
      <div className="container-zt" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
        <Link to={homePath} aria-label="Zantravina" data-testid="header-logo">
          <Logo />
        </Link>

        <nav className="zt-nav-desktop" style={{ gap: 32, alignItems: 'center' }}>
          {nav.map((n) => (
            <a key={n.label} href={n.to} className="nav-link" style={{ fontSize: 15 }}>
              {n.label}
            </a>
          ))}
        </nav>
        <style>{`.zt-nav-desktop { display: none; } .zt-cta-desktop { display: none; } @media (min-width: 900px){ .zt-nav-desktop { display: flex !important; } .zt-cta-desktop { display: inline-flex !important; } .zt-mobile-only { display: none !important; } }`}</style>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="btn-ghost"
              aria-label={t('nav.language')}
              data-testid="lang-toggle"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 999, border: '1px solid var(--line)', background: 'transparent', cursor: 'pointer', color: 'var(--ink)' }}
            >
              <Globe size={16} />
              <span style={{ fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{lang}</span>
            </button>
            {langOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: 6, minWidth: 140, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                {['ar', 'en'].map((L) => (
                  <button
                    key={L}
                    onClick={() => { switchLanguage(L); setLangOpen(false); }}
                    data-testid={`lang-${L}`}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: L === lang ? 'var(--paper-2)' : 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 14, color: 'var(--ink)' }}
                  >
                    {L === 'ar' ? 'Español (AR)' : 'English'}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a href={anchor('contact')} className="btn btn-primary zt-cta-desktop" data-testid="header-cta">
            {t('nav.contact')}
          </a>

          <button
            className="zt-mobile-only"
            onClick={() => setOpen(true)}
            aria-label={t('nav.menu')}
            data-testid="open-menu"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 8, border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer' }}
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mobile-menu" role="dialog" aria-modal="true">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link to={homePath} onClick={() => setOpen(false)} aria-label="Zantravina">
              <Logo />
            </Link>
            <button onClick={() => setOpen(false)} aria-label={t('nav.close')} data-testid="close-menu" style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid var(--line)', background: 'transparent', cursor: 'pointer', color: 'var(--ink)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={20} />
            </button>
          </div>
          <div style={{ marginTop: 40 }}>
            {nav.map((n) => (
              <a key={n.label} href={n.to} onClick={() => setOpen(false)}>{n.label}</a>
            ))}
          </div>
          <div style={{ marginTop: 32, display: 'flex', gap: 8 }}>
            {['ar', 'en'].map((L) => (
              <button
                key={L}
                onClick={() => switchLanguage(L)}
                data-testid={`mob-lang-${L}`}
                style={{ padding: '10px 16px', borderRadius: 999, border: '1px solid var(--ink)', background: L === lang ? 'var(--ink)' : 'transparent', color: L === lang ? 'var(--paper)' : 'var(--ink)', cursor: 'pointer' }}
              >
                {L === 'ar' ? 'Español' : 'English'}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
